import { useState, useEffect, useRef } from 'react';
import { Sun, Moon, Menu, X, ArrowUp, Settings, Cookie, Palette, Terminal } from 'lucide-react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

import Navbar from './layout/Navbar';
import Home from './features/home/Home';
import About from './features/home/About';
import Skills from './features/home/Skills';
import Projects from './features/home/Projects';
import Contact from './features/home/Contact';
import MobileWarning from './components/MobileWarning';
import FloatingSocialBar from './components/FloatingSocialBar';
import { trackPageView } from './utils/analytics';
import StructuredData from './components/StructuredData';
import DateTimeDisplay from './components/DateTimeDisplay';
import Footer, { TermsContentModal, PrivacyContentModal } from './layout/Footer';
import Notification from './components/Notification';
import NotFoundPage from './pages/NotFoundPage';
import { safeStorage } from './utils/storage';
import React, { Suspense, lazy } from 'react';

// Lazy loaded components (Code Splitting)
// // const AIChatbot = lazy(() => import('./features/chatbot/AIChatbot'));
const OfflineOverlay = lazy(() => import('./components/OfflineOverlay'));

const isSearchBot = typeof navigator !== 'undefined' && /googlebot|bingbot|yandexbot|duckduckbot|slurp|baiduspider|skypeuripreview|facebookexternalhit|twitterbot/i.test(navigator.userAgent);

function App() {
      const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [language, setLanguage] = useState('en');
  const [showScrollArrow, setShowScrollArrow] = useState(true);
  const [activeNotification, setActiveNotification] = useState<{ message: string; id: number } | null>(null);
  const [isDevToolsDetected, setIsDevToolsDetected] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  
  // Refs for scroll elements
  const backToTopRef = useRef<HTMLButtonElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const touchStartX = useRef<number | null>(null);

  // --- THAO TÁC VUỐT (SWIPE) ĐỂ MỞ/ĐÓNG NAVIGATION ---
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null) return;
      const touchEndX = e.changedTouches[0].clientX;
      const deltaX = touchStartX.current - touchEndX;

      // Vuốt từ phải sang trái (mở) - chỉ khi vuốt từ mép phải (cách lề phải <= 80px)
      if (!mobileMenuOpen && deltaX > 50 && touchStartX.current > window.innerWidth - 80) {
        setMobileMenuOpen(true);
      }
      // Vuốt từ trái sang phải (đóng)
      else if (mobileMenuOpen && deltaX < -50) {
        setMobileMenuOpen(false);
      }
      touchStartX.current = null;
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [mobileMenuOpen]);

  // --- AUTOMATIC URL CLEANUP (Security) ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('admin_key')) {
      // Clear the sensitive parameter from the URL bar without reloading
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // --- SWIPE HINT TIMER ---
  useEffect(() => {
    let timer1: NodeJS.Timeout;
    let timer2: NodeJS.Timeout;

    const cycle = () => {
      setShowSwipeHint(true);
      timer1 = setTimeout(() => {
        setShowSwipeHint(false);
        timer2 = setTimeout(cycle, 15000); // Wait 15s before showing again
      }, 3000); // Show for 3s
    };

    // Initial delay of 1s
    const initialDelay = setTimeout(() => {
      cycle();
    }, 1000);

    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // --- DEVTOOLS DETECTION LISTENER ---
  useEffect(() => {
    const handleDevTools = () => {
      setIsDevToolsDetected(true);
    };
    window.addEventListener('DEVTOOLS_DETECTED', handleDevTools);
    return () => window.removeEventListener('DEVTOOLS_DETECTED', handleDevTools);
  }, []);

  // --- BẮT ĐẦU: HIỂN THỊ THÔNG BÁO CONSOLE CHUYÊN NGHIỆP ---
  useEffect(() => {
    // Chỉ chạy trên trình duyệt
    if (typeof window !== 'undefined') {
      const isDarkMode = document.documentElement.classList.contains('dark');
      const primaryColor = isDarkMode ? '#60A5FA' : '#3B82F6'; // Blue
      const secondaryColor = '#EC4899'; // Pink
      
      const titleStyle = `
        font-family: Arial, sans-serif;
        font-size: 50px;
        font-weight: 900;
        color: ${primaryColor};
        text-shadow: 3px 3px 0px #000, 6px 6px 0px rgba(0,0,0,0.1);
        padding: 10px;
      `;
      
      const warningStyle = `
        font-family: "Inter", sans-serif;
        font-size: 16px;
        font-weight: bold;
        color: #EF4444;
        background: #FEE2E2;
        padding: 8px 12px;
        border-radius: 4px;
        line-height: 1.5;
        border-left: 5px solid #EF4444;
      `;
      
      const infoStyle = `
        font-family: "Inter", sans-serif;
        font-size: 14px;
        color: ${isDarkMode ? '#94A3B8' : '#64748B'};
        line-height: 1.5;
      `;
      
      const signatureStyle = `
        font-family: "Inter", sans-serif;
        font-size: 12px;
        font-weight: bold;
        color: ${secondaryColor};
        text-transform: uppercase;
        letter-spacing: 1px;
        border-top: 1px solid ${isDarkMode ? '#334155' : '#E2E8F0'};
        padding-top: 10px;
        margin-top: 10px;
      `;

      setTimeout(() => {
        console.clear();
        console.log('%cSTOP!', titleStyle);
        
        console.log(
          '%cĐây là một tính năng của trình duyệt dành cho các nhà phát triển. Nếu ai đó bảo bạn sao chép-dán mã vào đây để "hack" tài khoản của người khác hoặc "mở khóa" tính năng, đó là một hành vi lừa đảo và sẽ cho phép họ truy cập vào thông tin của bạn.',
          warningStyle
        );
        
        console.log(
          '%cStop! This is a browser feature intended for developers. If someone told you to copy-paste code here to "hack" someone\'s account or "unlock" a feature, it is a scam and will give them access to your information.',
          warningStyle
        );

        console.log('\n');
        
        console.log(
          '%cBạn là nhà tuyển dụng? Hãy xem qua portfolio của mình và liên hệ nếu thấy phù hợp nhé!',
          infoStyle
        );
        
        console.log(
          '%cAre you a recruiter? Feel free to browse my portfolio and contact me if you are interested!',
          infoStyle
        );

        console.log(
          '%cPhát triển bởi Tai Nguyen / Developed by Tai Nguyen',
          signatureStyle
        );
      }, 1000);
    }
  }, []);
  // --- KẾT THÚC: THÔNG BÁO CONSOLE ---
  
  // --- STATE QUẢN LÝ MODAL ---
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);     // State mới cho Terms
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false); // State mới cho Privacy
  
  // --- Sitelinks Routing & Smooth Scrolling ---
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname.substring(1).toLowerCase();
    const sectionId = path || 'home';
    const validSections = ['home', 'about', 'skills', 'projects', 'contact'];
    
    if (validSections.includes(sectionId)) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (sectionId === 'home') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [location.pathname]);
  
  // --- STATE QUẢN LÝ COOKIE VÀ SETTINGS ---
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(() => {
    return !safeStorage.getItem('cookie_consent_seen');
  });
  const [analyticsCookies, setAnalyticsCookies] = useState(() => {
    return safeStorage.getItem('analytics_cookies') !== 'false';
  });
  const [isPinkTheme, setIsPinkTheme] = useState(false);
  const [tempPinkTheme, setTempPinkTheme] = useState(false);
  
  // --- STATE CHO MINI CLI TERMINAL ---
  const [terminalInput, setTerminalInput] = useState('');
  const [terminalStatus, setTerminalStatus] = useState<{ type: 'success' | 'error' | 'info' | null, message: string }>({ type: null, message: '' });

  const handleAcceptAllCookies = () => {
    setAnalyticsCookies(true);
    safeStorage.setItem('analytics_cookies', 'true');
    safeStorage.setItem('cookie_consent_seen', 'true');
    setShowCookieConsent(false);
  };



  const handleTerminalCommand = (e: React.FormEvent) => {
    e.preventDefault();
    const cmd = terminalInput.trim().toLowerCase();
    if (!cmd) return;

    const sections = ['home', 'about', 'skills', 'projects', 'contact'];
    
    if (cmd === 'help') {
      setTerminalStatus({ type: 'info', message: 'Commands: cd [section], ls, clear, help' });
    } else if (cmd === 'ls') {
      setTerminalStatus({ type: 'info', message: `Sections: ${sections.join(', ')}` });
    } else if (cmd === 'clear') {
      setTerminalStatus({ type: null, message: '' });
    } else if (cmd.startsWith('cd ') || cmd.startsWith('goto ')) {
      const target = cmd.split(' ')[1];
      if (sections.includes(target)) {
        const element = document.getElementById(target);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setTerminalStatus({ type: 'success', message: `Successfully navigated to ${target}.` });
          // Tự động đóng modal sau 1.5s để người dùng thấy kết quả
          setTimeout(() => {
            setIsSettingsOpen(false);
            setTerminalStatus({ type: null, message: '' });
          }, 1500);
        }
      } else {
        setTerminalStatus({ type: 'error', message: `Error: Section "${target}" not found.` });
      }
    } else {
      setTerminalStatus({ type: 'error', message: `Syntax Error: "${cmd}" is not recognized.` });
    }
    setTerminalInput('');
  };

  // --- EFFECT QUẢN LÝ SCROLL LOCK KHI MỞ SETTINGS ---
  useEffect(() => {
    if (isSettingsOpen) {
      const originalBodyStyle = document.body.style.overflow;
      const originalHtmlStyle = document.documentElement.style.overflow;
      
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalBodyStyle;
        document.documentElement.style.overflow = originalHtmlStyle;
      };
    }
  }, [isSettingsOpen]);
  
  const activeSectionRef = useRef('home');

  useEffect(() => {
    // Logic chặn phím tắt macOS và Windows
    const preventDevTools = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();

      // 1. Chặn các phím chức năng cơ bản: F12, F5
      if (e.key === 'F12' || e.key === 'F5') { 
        e.preventDefault(); 
        return false; 
      }

      // 2. Chặn tổ hợp phím trên Windows (Dùng phím Ctrl)
      if (e.ctrlKey) {
        if (
          e.key === 'F5' || // Ctrl + F5
          key === 'u' || // Ctrl + U (View Source)
          (e.shiftKey && ['i', 'c', 'j', 'r'].includes(key)) // Ctrl + Shift + I / C / J / R
        ) {
          e.preventDefault();
          return false;
        }
      }

      // 3. Chặn tổ hợp phím trên macOS (Dùng phím Command / Meta)
      if (e.metaKey) {
        if (
          ['r', 'f'].includes(key) || // Cmd + R (Reload), Cmd + F (Find)
          (e.shiftKey && ['c', 'r'].includes(key)) || // Cmd + Shift + C / R
          (e.altKey && ['i', 'j', 'u', 'f', 'r'].includes(key)) // Cmd + Option (Alt) + I / J / U / F / R
        ) {
          e.preventDefault();
          return false;
        }
      }
    };
    
    const preventCopy = (e: Event) => { e.preventDefault(); return false; };
    const preventRightClick = (e: MouseEvent) => { e.preventDefault(); return false; };

    document.addEventListener('copy', preventCopy);
    document.addEventListener('contextmenu', preventRightClick);
    document.addEventListener('selectstart', preventCopy);
    document.addEventListener('keydown', preventDevTools);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const message = 'ntainguyenit.pages.dev cho biết bạn muốn rời khỏi trang web? Đừng quên liên hệ với Tai Nguyen nhé!';
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    const savedTheme = safeStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }

    const savedLanguage = safeStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }

    // Tối ưu hóa hiệu năng Scroll bằng requestAnimationFrame
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const totalScroll = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          const currentScroll = document.documentElement.scrollTop;
          
          const progress = (currentScroll / totalScroll) * 100;

          if (backToTopRef.current) {
            if (progress > 20) {
              backToTopRef.current.style.opacity = '1';
              backToTopRef.current.style.pointerEvents = 'auto';
            } else {
              backToTopRef.current.style.opacity = '0';
              backToTopRef.current.style.pointerEvents = 'none';
            }
          }

          if (circleRef.current) {
            const circumference = 138.23;
            const offset = circumference - (progress / 100) * circumference;
            circleRef.current.style.strokeDashoffset = offset.toString();
          }
          
          setShowScrollArrow(currentScroll <= 100);

          const path = location.pathname.toLowerCase();
          const validPaths = ['/', '/home', '/about', '/skills', '/projects', '/contact'];
          if (validPaths.includes(path)) {
              const sections = ['home', 'about', 'skills', 'projects', 'contact'];
              for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                  const rect = element.getBoundingClientRect();
                  const offset = 150; // threshold greater than scroll-padding-top
                  if (rect.top <= offset && rect.bottom >= offset) {
                    if (activeSectionRef.current !== section) {
                      activeSectionRef.current = section;
                      setActiveSection(section);
                      trackPageView(`/#${section}`, `${section.charAt(0).toUpperCase() + section.slice(1)} - Tai Nguyen Portfolio`);
                    }
                    break;
                  }
                }
              }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    trackPageView(location.pathname, document.title);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('contextmenu', preventRightClick);
      document.removeEventListener('selectstart', preventCopy);
      document.removeEventListener('keydown', preventDevTools);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    if (darkMode) {
      document.documentElement.classList.remove('dark');
      safeStorage.setItem('theme', 'light');
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        setActiveNotification({ message: language === 'vi' ? 'Chế độ sáng' : 'Light Mode', id: Date.now() });
      }
    } else {
      document.documentElement.classList.add('dark');
      safeStorage.setItem('theme', 'dark');
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        setActiveNotification({ message: language === 'vi' ? 'Chế độ tối' : 'Dark Mode', id: Date.now() });
      }
    }
  };

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'vi' : 'en';
    setLanguage(newLanguage);
    safeStorage.setItem('language', newLanguage);
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      setActiveNotification({ 
        message: newLanguage === 'vi' ? 'Đã chuyển tiếng Việt' : 'Switched to English', 
        id: Date.now() 
      });
    }
  };

  // --- WebMCP Integration Events ---
  useEffect(() => {
    const handleToggleDark = () => toggleDarkMode();
    const handleToggleLang = () => toggleLanguage();
    const handleScroll = (e: CustomEvent) => {
      const element = document.getElementById(e.detail);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    };

    window.addEventListener('mcp_toggle_dark_mode', handleToggleDark);
    window.addEventListener('mcp_toggle_language', handleToggleLang);
    window.addEventListener('mcp_scroll_to_section', handleScroll);

    return () => {
      window.removeEventListener('mcp_toggle_dark_mode', handleToggleDark);
      window.removeEventListener('mcp_toggle_language', handleToggleLang);
      window.removeEventListener('mcp_scroll_to_section', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode, language]);

  // --- EFFECT QUẢN LÝ PINK THEME ---
  useEffect(() => {
    if (isPinkTheme) {
      document.documentElement.classList.add('theme-pink');
    } else {
      document.documentElement.classList.remove('theme-pink');
    }
  }, [isPinkTheme]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isHomePage = location.pathname === '/' || 
    ['/home', '/about', '/skills', '/projects', '/contact'].includes(location.pathname.toLowerCase());

  if (isDevToolsDetected) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-red-600 text-white p-6 text-center z-[999999] fixed inset-0">
        <h1 className="text-4xl font-black mb-4 uppercase tracking-wider">Security Warning</h1>
        <p className="text-xl mb-2 font-medium">Developer tools detection triggered.</p>
        <p className="text-lg opacity-80">Your session is locked. The page will automatically reload in 5 minutes...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen transition-colors duration-300 overflow-x-clip">
        
        
        {isHomePage && <MobileWarning language={language} />}

        {activeNotification && (
          <Notification 
            key={activeNotification.id}
            message={activeNotification.message} 
            onClose={() => setActiveNotification(null)} 
          />
        )}

        <div className="fixed inset-0 overflow-hidden -z-10">
          <div className={`absolute inset-0 bg-gradient-to-b ${isPinkTheme ? 'from-pink-50 to-pink-100 dark:from-pink-950 dark:to-pink-900' : 'from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900'}`}></div>
           <div className="absolute bottom-0 left-0 right-0 h-[30vh]">
            <svg className="w-full h-full" viewBox="0 0 1440 320" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0,224L60,213.3C120,203,240,181,360,181.3C480,181,600,203,720,213.3C840,224,960,224,1080,208C1200,192,1320,160,1380,144L1440,128L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" fill="url(#gradient1)" fillOpacity="0.1"></path>
              <defs><linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={isPinkTheme ? "#EC4899" : "#3B82F6"} /><stop offset="100%" stopColor={isPinkTheme ? "#DB2777" : "#2563EB"} /></linearGradient></defs>
            </svg>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[25vh]">
            <svg className="w-full h-full" viewBox="0 0 1440 320" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" fill="url(#gradient2)" fillOpacity="0.15"></path>
              <defs><linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={isPinkTheme ? "#DB2777" : "#2563EB"} /><stop offset="100%" stopColor={isPinkTheme ? "#EC4899" : "#3B82F6"} /></linearGradient></defs>
            </svg>
          </div>
          <div className="absolute top-0 left-0 right-0 h-[20vh]">
            <svg className="w-full h-full" viewBox="0 0 1440 320" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,117.3C672,107,768,117,864,144C960,171,1056,213,1152,218.7C1248,224,1344,192,1392,176L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" fill="url(#gradient3)" fillOpacity="0.1"></path>
              <defs><linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={isPinkTheme ? "#EC4899" : "#3B82F6"} /><stop offset="100%" stopColor={isPinkTheme ? "#F472B6" : "#60A5FA"} /></linearGradient></defs>
            </svg>
          </div>
        </div>

        <div className="fixed top-[44px] right-8 z-40 hidden md:flex items-center space-x-3">
          <div className="relative group">
            <button onClick={toggleLanguage} className="flex items-center justify-center px-4 py-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-300">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{language === 'en' ? 'ENG' : 'VIE'}</span>
            </button>
            <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl hidden md:block">
              {language === 'vi' ? 'Ngôn ngữ' : 'Language'}
              <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
            </div>
          </div>

          <div className="relative group">
            <button onClick={toggleDarkMode} className="flex items-center justify-center w-[48px] h-[48px] bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-300">
              {darkMode ? <Sun size={20} className="text-gray-800 dark:text-gray-200" /> : <Moon size={20} className="text-gray-800 dark:text-gray-200" />}
            </button>
            <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl hidden md:block">
              {language === 'vi' ? (darkMode ? 'Chế độ sáng' : 'Chế độ tối') : (darkMode ? 'Light mode' : 'Dark mode')}
              <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
            </div>
          </div>
          
          <div className="relative group">
            <button onClick={() => {
               setTempPinkTheme(isPinkTheme);
               setIsSettingsOpen(true);
             }} className="relative flex items-center justify-center w-[48px] h-[48px] bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-300" aria-label="Settings">
              <Settings size={20} className="text-gray-800 dark:text-gray-200" />
              {showCookieConsent && (
                <span className="absolute top-0 right-0 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500 border-2 border-white dark:border-gray-800"></span>
                </span>
              )}
            </button>
            <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-max px-3 py-1.5 bg-gray-900 dark:bg-black text-white text-[11px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl hidden md:block">
              {language === 'vi' ? 'Cài đặt' : 'Settings'}
              <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-black transform rotate-45"></div>
            </div>

            {/* Subtle Cookie Consent Tooltip */}
            {showCookieConsent && (
              <div className="absolute top-[60px] right-0 w-52 bg-white dark:bg-gray-800 p-3.5 rounded-xl shadow-[0_10px_40px_-10px_rgba(37,99,235,0.3)] border border-gray-100 dark:border-gray-700 animate-[fade-in-down_0.3s_ease-out]">
                <div className="absolute -top-2 right-5 w-4 h-4 bg-white dark:bg-gray-800 rotate-45 border-l border-t border-gray-100 dark:border-gray-700"></div>
                <div className="relative z-10 flex flex-col gap-2.5">
                  <div className="flex items-start gap-2">
                    <Cookie size={16} className="text-blue-500 shrink-0 mt-0.5" />
                    <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 leading-snug">We use cookies to improve your experience.</span>
                  </div>
                  <button onClick={handleAcceptAllCookies} className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold rounded-lg transition-colors shadow-sm">
                    Accept All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {isHomePage && (
            <div className={`md:hidden fixed top-4 right-4 z-50 flex items-center gap-2 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
              
              {/* Swipe Hint */}
              {showSwipeHint && showScrollArrow && (
                 <div className="text-[13px] text-gray-500/90 dark:text-gray-400/90 font-medium whitespace-nowrap flex items-center select-none animate-in fade-in duration-1000 transition-opacity">
                   <span className="inline-block mr-1.5 text-blue-500 text-lg font-bold" style={{ animation: 'slideLeft 1.5s infinite ease-in-out' }}>
                     <style>{`@keyframes slideLeft { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-4px); } }`}</style>
                     &lt;&lt;
                   </span>
                   {language === 'vi' ? 'Vuốt nhẹ sang trái để mở điều hướng' : 'Swipe left to open navigation'}
                 </div>
              )}

              {/* Hamburger Button */}
              <button className="w-[40px] h-[40px] flex items-center justify-center bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-all duration-300 border-b-2 border-blue-500 dark:border-blue-400" onClick={() => setMobileMenuOpen(true)}>
                <Menu size={20} className="text-blue-600 dark:text-blue-400" />
              </button>
            </div>
        )}

        <button ref={backToTopRef} className="fixed bottom-4 right-4 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all duration-300 hover:bg-blue-700 hover:scale-110 opacity-0 pointer-events-none" onClick={scrollToTop}>
          <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 48 48">
            <circle
              cx="24"
              cy="24"
              r="22"
              fill="none"
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth="2"
            />
            <circle
              ref={circleRef}
              cx="24"
              cy="24"
              r="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="138.23"
              strokeDashoffset="138.23"
              strokeLinecap="round"
              className="text-white transition-all duration-75"
            />
          </svg>
          <ArrowUp size={20} className="relative z-10" />
        </button>

        {isHomePage && <Navbar activeSection={activeSection} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} language={language} darkMode={darkMode} toggleDarkMode={toggleDarkMode} toggleLanguage={toggleLanguage} />}
        {isHomePage && <div className="hidden md:block"><FloatingSocialBar /></div>}
        {isHomePage && <DateTimeDisplay language={language} isVisible={activeSection === 'home'} />}

        {/* --- TRUYỀN HÀM MỞ TERMS VÀ PRIVACY VÀO CONTACT MODAL --- */}
        {isContactModalOpen && (
            <Contact 
               language={language} 
               isModal={true} 
               onClose={() => setIsContactModalOpen(false)} 
               onOpenTerms={() => {
                  setIsContactModalOpen(false); // Đóng form Contact đi
                  setIsTermsOpen(true);         // Mở form Terms lên
               }}
               onOpenPrivacy={() => {
                  setIsContactModalOpen(false); 
                  setIsPrivacyOpen(true);
               }}
            />
        )}

        {/* --- HIỂN THỊ MODAL TERMS Ở ĐÂY --- */}
        {isTermsOpen && (
            <TermsContentModal language={language} onClose={() => setIsTermsOpen(false)} />
        )}

        {/* --- HIỂN THỊ MODAL PRIVACY Ở ĐÂY --- */}
        {isPrivacyOpen && (
            <PrivacyContentModal language={language} onClose={() => setIsPrivacyOpen(false)} />
        )}


        {/* --- QUẢN LÝ COOKIE (POPUP CŨ ĐÃ XÓA, CHUYỂN VÀO SETTINGS) --- */}

        {/* --- SETTINGS MODAL --- */}
        {isSettingsOpen && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsSettingsOpen(false)}></div>
            <div className="bg-white dark:bg-gray-900 w-full max-w-3xl rounded-3xl shadow-2xl relative z-10 overflow-hidden p-6 md:p-8 border border-gray-100 dark:border-gray-800 animate-[fade-in-up_0.3s_ease-out]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl md:text-2xl font-bold dark:text-white flex items-center gap-3"><Settings className="text-blue-500" /> {language === 'vi' ? 'Cài đặt' : 'Settings'}</h2>
                <button onClick={() => setIsSettingsOpen(false)} className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-full hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30 transition-colors"><X size={20} /></button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                {/* --- CỘT TRÁI: APPEARANCE & COOKIES --- */}
                <div>
                  <div className="mb-6">
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-4 pl-1">{language === 'vi' ? 'Giao diện' : 'Appearance'}</h3>
                    <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-pink-200 dark:hover:border-pink-900/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                          <Palette size={18} className="text-pink-500" />
                        </div>
                        <div>
                          <div className="font-bold text-sm text-gray-900 dark:text-white">{language === 'vi' ? 'Chủ đề Hồng' : 'Pink Theme'}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">{language === 'vi' ? 'Bật giao diện màu hồng nhạt' : 'Toggle soft pink interface'}</div>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={tempPinkTheme} onChange={(e) => setTempPinkTheme(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-pink-500 shadow-inner"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-4 pl-1">{language === 'vi' ? 'Tuỳ chọn Cookie' : 'Cookie Preferences'}</h3>
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                        <div>
                          <div className="font-bold text-sm text-gray-900 dark:text-white">{language === 'vi' ? 'Thiết yếu' : 'Necessary'}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{language === 'vi' ? 'Cần thiết cho hoạt động của web' : 'Essential for functionality'}</div>
                        </div>
                        <div className="text-[10px] font-bold px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 rounded-md">{language === 'vi' ? 'Luôn bật' : 'Always On'}</div>
                      </div>

                      <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                        <div>
                          <div className="font-bold text-sm text-gray-900 dark:text-white">{language === 'vi' ? 'Phân tích' : 'Analytics'}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{language === 'vi' ? 'Giúp mình cải thiện trải nghiệm' : 'Help us improve your experience'}</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" checked={analyticsCookies} onChange={(e) => {
                            setAnalyticsCookies(e.target.checked);
                            safeStorage.setItem('analytics_cookies', e.target.checked.toString());
                          }} />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-500 shadow-inner"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- CỘT PHẢI: MINI CLI --- */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400 pl-1 flex items-center gap-2">
                      <Terminal size={14} /> Mini CLI 
                      <span className="italic font-normal lowercase opacity-70">({language === 'vi' ? 'chế độ dành cho nhà phát triển' : 'developer mode'})</span>
                    </h3>
                  </div>
                  
                  <div className="bg-black/90 dark:bg-black rounded-xl p-5 font-mono text-[12px] shadow-inner overflow-hidden border border-gray-800 h-[220px] flex flex-col">
                    <div className="flex-1 overflow-y-auto mb-2 custom-scrollbar">
                      <div className="text-gray-500 whitespace-pre-line tracking-tight leading-relaxed">
                        {terminalStatus.type === null && (
                          <span className="text-blue-400 opacity-60 italic">Type "help" to see available commands...</span>
                        )}
                        
                        {terminalStatus.type === 'info' && <span className="text-blue-400"># {terminalStatus.message}</span>}
                        {terminalStatus.type === 'success' && <span className="text-green-500">{terminalStatus.message}</span>}
                        {terminalStatus.type === 'error' && <span className="text-red-500">{terminalStatus.message}</span>}
                      </div>
                    </div>
                    
                    <form onSubmit={handleTerminalCommand} className="flex items-center gap-2 text-white border-t border-gray-800 pt-3">
                      <span className="text-green-500 font-bold whitespace-nowrap">visitor@tai-nguyen:~$</span>
                      <input 
                        type="text" 
                        value={terminalInput}
                        onChange={(e) => setTerminalInput(e.target.value)}
                        placeholder="..."
                        className="bg-transparent border-none outline-none flex-1 text-white caret-blue-500 placeholder:opacity-20"
                        autoFocus={isSettingsOpen}
                      />
                    </form>
                  </div>
                  <div className="mt-3 px-1">
                    <p className="text-[10px] leading-relaxed text-gray-400 dark:text-gray-500 italic">
                      <span className="text-amber-500 dark:text-amber-400 font-bold not-italic">Warning:</span> {language === 'vi' 
                        ? 'Tính năng được phát triển bởi Tai Nguyen nhằm tối ưu hóa trải nghiệm khám phá. Vui lòng không sử dụng cho mục đích can thiệp hệ thống hoặc các hành vi vi phạm pháp luật.' 
                        : 'This feature was developed by Tai Nguyen to optimize your exploration. Please do not use it for system interference or any illegal activities.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={() => {
                  setIsPinkTheme(tempPinkTheme);
                  safeStorage.setItem('cookie_consent_seen', 'true');
                  setShowCookieConsent(false);
                  setIsSettingsOpen(false);
                }} className="flex-1 py-3.5 border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 text-[13px] font-bold rounded-2xl transition-colors">
                  {language === 'vi' ? 'Lưu' : 'Save'}
                </button>
                <button onClick={() => {
                  setIsPinkTheme(tempPinkTheme);
                  handleAcceptAllCookies();
                  setIsSettingsOpen(false);
                }} className="flex-[1.5] py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white text-[13px] font-bold rounded-2xl transition-colors shadow-lg hover:shadow-blue-500/30">
                  {language === 'vi' ? 'Chấp nhận tất cả' : 'Accept All'}
                </button>
              </div>
            </div>
          </div>
        )}

        <Routes>
            <Route path="/" element={<Navigate to="/Home" replace />} />
            <Route path="/:section" element={
                <main className="transition-all duration-300 relative z-10">
                    <Home 
                      language={language} 
                      showScrollArrow={showScrollArrow} 
                      onOpenContact={() => setIsContactModalOpen(true)} 
                      />
                    <About language={language} />
                    <Skills language={language} />
                    <Projects language={language} />
                    
                    {/* --- TRUYỀN HÀM MỞ TERMS VÀ PRIVACY VÀO CONTACT SECTION --- */}
                    <Contact 
                       language={language} 
                       onOpenTerms={() => setIsTermsOpen(true)}
                       onOpenPrivacy={() => setIsPrivacyOpen(true)}
                    />
                    
                    <Footer language={language} />
                    <StructuredData language={language} />
                </main>
            } />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <Suspense fallback={null}>
          {/* <AIChatbot /> */}
          <OfflineOverlay />
        </Suspense>
        
    </div>
  );
}

export default App;
